'use client';

import { useEffect, type CSSProperties } from 'react';
import { ErrorFallbackContent, useChunkRecovery } from '@/components/ErrorFallback';
import { reportRumError } from '@/lib/datadog/rum';

/**
 * Route-segment error boundary. Catches render errors in any page under the
 * root layout (the layout/shell stays mounted).
 *
 * The dominant case is a ChunkLoadError from deploy skew (see lib/chunkReload.ts)
 * — most often the Plotly chunk 404ing when an analysis tab is first opened
 * after a new deployment — which auto-reloads. Everything else gets a
 * try-again / reload fallback.
 *
 * The auto-reload is intentional and immediate: this boundary sits at the app/
 * level, so by the time it renders the entire page subtree has already thrown
 * and unmounted — there is no in-page state (e.g. a chat draft) left to lose.
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = useChunkRecovery(error);
  useEffect(() => {
    reportRumError(error);
  }, [error]);

  return (
    <div style={wrap}>
      <ErrorFallbackContent chunkError={chunkError} onReset={reset} />
    </div>
  );
}

const wrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: 24,
};
